<!-- Backdrop -->
@if (dialogService.isOpen('menu')) {
  <button
    class="fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 focus:outline-none lg:hidden"
    (click)="closeMenu()"
    (keydown.escape)="closeMenu()"
    aria-label="Close menu"
  ></button>
}

<!-- Menu -->
<div
  class="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-primary-background transition-all duration-300 ease-in-out lg:hidden"
  [ngClass]="{
    'pointer-events-auto translate-x-0 opacity-100':
      dialogService.isOpen('menu'),
    'pointer-events-none translate-x-full opacity-0':
      !dialogService.isOpen('menu'),
  }"
  role="dialog"
  aria-modal="true"
  aria-label="Mobile menu"
>
  <div class="flex min-h-full flex-col">
    <!-- Header -->
    <div
      class="flex items-center justify-between border-b border-secondary-border p-4"
    >
      <img
        class="h-6"
        src="assets/images/logo_dh.svg"
        alt="Den Hollander Logo"
        loading="lazy"
      />
      <button
        class="flex items-center text-primary-text"
        (click)="closeMenu()"
        aria-label="Close menu"
      >
        {{ 'components.header.close' | translate }}
        <img class="ml-2 h-4 w-4" src="/assets/icons/close.svg" alt="Close" />
      </button>
    </div>

    <!-- Navigation Items -->
    <div class="flex-grow overflow-y-auto">
      <nav class="flex flex-col">
        @for (
          item of navItems();
          track item.url ?? item.dropdown ?? item.name
        ) {
          @if (item.dropdown !== 'languageMenu') {
            @if (item.dropdown) {
              <!-- Dropdown items like Producten -->
              <button
                class="flex w-full items-center justify-between border-b border-secondary-border p-4 text-base font-medium text-primary-text"
                [class.text-orange-500]="
                  dialogService.isOpen(item.dropdown) || isAnyChildActive(item)
                "
                (click)="toggleDropdown(item.dropdown, $event)"
              >
                {{ item.name | translate }}
                <img
                  class="h-4 w-4 transition-transform duration-300"
                  [ngClass]="{
                    'rotate-180': dialogService.isOpen(item.dropdown),
                  }"
                  src="/assets/icons/arrow_down.svg"
                  alt="Arrow Down"
                />
              </button>
              <div
                class="grid transition-[grid-template-rows] duration-300"
                [ngClass]="{
                  'grid-rows-[1fr]': dialogService.isOpen(item.dropdown),
                  'grid-rows-[0fr]': !dialogService.isOpen(item.dropdown),
                }"
              >
                <div class="overflow-hidden">
                  <div class="bg-primary-background">
                    @for (
                      dropdownItem of item.dropdownItems;
                      track dropdownItem.url ?? dropdownItem.name
                    ) {
                      <a
                        class="block px-6 py-4 text-base font-normal text-secondary-text hover:text-orange-500"
                        [routerLink]="dropdownItem.url"
                        [class.text-orange-500]="
                          isRouteActive(dropdownItem.url)
                        "
                        (click)="handleDropdownItemClick(item)"
                      >
                        {{ dropdownItem.name | translate }}
                      </a>
                    }
                  </div>
                </div>
              </div>
            } @else {
              <!-- Regular nav items -->
              <a
                class="block border-b border-secondary-border p-4 text-base font-medium text-primary-text hover:text-orange-500"
                [routerLink]="item.url"
                [class.text-orange-500]="isRouteActive(item.url)"
                (click)="closeMenu()"
              >
                {{ item.name | translate }}
              </a>
            }
          }
        }

        <!-- Profile Section for Authenticated Users -->
        @if (isAuthenticated) {
          <button
            class="flex w-full items-center justify-between border-b border-secondary-border p-4 text-base font-medium text-primary-text"
            [class.text-orange-500]="dialogService.isOpen('profile')"
            (click)="toggleDropdown('profile', $event)"
          >
            <div class="flex items-center gap-4">
              <dhp-profile-icon [user]="currentUser()"></dhp-profile-icon>
              <div class="flex flex-col">
                <span>{{
                  userName ?? ('components.header.noUsername' | translate)
                }}</span>
                <span
                  class="text-sm text-secondary-text"
                  [ngClass]="{ italic: !organization }"
                >
                  {{
                    organization ??
                      ('components.header.noOrganization' | translate)
                  }}
                </span>
              </div>
            </div>
            <img
              class="h-4 w-4 transition-transform duration-300"
              [ngClass]="{ 'rotate-180': dialogService.isOpen('profile') }"
              src="/assets/icons/arrow_down.svg"
              alt="Arrow Down"
            />
          </button>
          <div
            class="grid transition-[grid-template-rows] duration-300"
            [ngClass]="{
              'grid-rows-[1fr]': dialogService.isOpen('profile'),
              'grid-rows-[0fr]': !dialogService.isOpen('profile'),
            }"
          >
            <div class="overflow-hidden">
              <div class="bg-primary-background">
                <a
                  class="block px-6 py-4 text-base font-normal text-secondary-text hover:text-orange-500"
                  [routerLink]="['/account']"
                  (click)="closeMenu()"
                  routerLinkActive="text-orange-500"
                >
                  {{ 'components.header.profileSettings' | translate }}
                </a>
                <a
                  class="block px-6 py-4 text-base font-normal text-secondary-text hover:text-orange-500"
                  [routerLink]="['/organization']"
                  (click)="closeMenu()"
                  routerLinkActive="text-orange-500"
                >
                  {{ 'components.header.organization' | translate }}
                </a>
                <a
                  class="block px-6 py-4 text-base font-normal text-secondary-text hover:text-orange-500"
                  [routerLink]="['/help']"
                  (click)="closeMenu()"
                  routerLinkActive="text-orange-500"
                >
                  {{ 'components.header.helpAndInfo' | translate }}
                </a>
                <button
                  class="block w-full px-6 py-4 text-left text-base font-normal text-secondary-text hover:text-orange-500"
                  (click)="logout()"
                >
                  {{ 'components.header.logout' | translate }}
                </button>
              </div>
            </div>
          </div>
        } @else {
          <!-- Authentication Buttons for Unauthenticated Users -->
          <div class="flex flex-col gap-2 px-4 py-4">
            <button
              class="btn-secondary btn w-full"
              [routerLink]="['/auth/register']"
              (click)="closeMenu()"
            >
              {{ 'components.header.register' | translate }}
            </button>
            <button
              class="btn btn-text-only"
              [routerLink]="['/auth/login']"
              [queryParams]="{ returnUrl: returnUrl }"
              (click)="closeMenu()"
            >
              {{ 'components.header.login' | translate }}
            </button>
          </div>
        }
      </nav>
    </div>

    <!-- Footer Links -->
    <div class="mt-auto border-t border-secondary-border">
      <!-- Additional Links -->
      <div class="px-4 py-2">
        <div class="flex flex-col">
          <a
            class="py-2 text-base font-normal text-primary-text underline hover:text-orange-500"
            (click)="$event.preventDefault(); toggleSearch()"
            href=""
          >
            {{ 'components.mobileMenu.search' | translate }}
          </a>
          <button
            class="py-2 text-left text-base font-normal text-primary-text underline hover:text-orange-500"
            (click)="changeLanguage()"
          >
            {{ 'components.mobileMenu.changeLanguage' | translate }}
          </button>
          <a
            class="py-2 text-base font-normal text-primary-text underline hover:text-orange-500"
            [routerLink]="['/pricing']"
            [class.text-orange-500]="isRouteActive('/pricing')"
            (click)="closeMenu()"
          >
            {{ 'components.mobileMenu.shoppingCart' | translate }}
          </a>
        </div>
      </div>
    </div>
  </div>
</div>
